import { faListCheck, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from '@tanstack/react-router'
import clsx from 'clsx'
import { type ReactElement, useContext, useMemo, useRef, useState } from 'react'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { container } from '~/components/Global/Interactions/Dropdown/Downdown.css'
import { Dropdown } from '~/components/Global/Interactions/Dropdown/Dropdown'
import {
  toolBarItemAttention,
  toolBarItemAttentionCount
} from '~/components/Layout/Header/Header.css'
import { getAsideActionGlobalRef } from '~/components/Scopes/Actions/Global/asideActionRef'
import { TaskAction } from '~/domains/tasks/components/TaskAction/TaskAction'
import { TaskCard } from '~/domains/tasks/components/TaskCard/TaskCard'
import { taskGridCompact } from '~/domains/tasks/components/TaskCard/TaskCard.css'
import { useTasks } from '~/domains/tasks/queries/tasks'
import { useTasksCount } from '~/domains/tasks/queries/tasksCount'
import { formatTasksCountSummary } from '~/domains/tasks/utils/formatTasksCountSummary'
import { AsideActionContext, AuthContext } from '~/utils/contextes'
import { buildV3Url } from '~/utils/legacy'
import {
  tasksDropdown,
  tasksFooter,
  tasksLoader,
  tasksStack,
  tasksTitle,
  tasksTitleCount,
  tasksZone
} from './TasksViewer.css'

const PREVIEW_COUNT = 10

type TasksViewerProps = {
  classname?: string
  currentUserId?: number
}

export const TasksViewer = ({
  classname,
  currentUserId
}: TasksViewerProps): ReactElement => {
  const [isOpen, setOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const allTasksRef = useRef<HTMLAnchorElement>(null)
  const currentUser = useContext(AuthContext)
  const { setAsideAction } = useContext(AsideActionContext)
  const userId = currentUser?.id ?? currentUserId ?? null
  const isLegacy = window.JobConfig !== undefined

  const { overdue, dueToday, total } = useTasksCount(userId)
  const countSummary = formatTasksCountSummary(overdue, dueToday)
  const { data, isLoading, isError } = useTasks({
    user: userId ?? undefined,
    itemsPerPage: PREVIEW_COUNT,
    isCompleted: false,
    enabled: isOpen && userId !== null
  })
  const tasks = useMemo(() => data?.pages[0]?.['hydra:member'] ?? [], [data])

  const openCreation = () => {
    setOpen(false)
    const action = <TaskAction defaultType="task" />
    if (isLegacy) {
      getAsideActionGlobalRef().current(action, {
        isWide: false,
        position: 'sidebar',
        availablePositions: ['sidebar']
      })
      return
    }
    setAsideAction({ action })
  }

  const allTasksButton = (
    <Button variation={ButtonVariation.Secondary} buttonSize="small">
      Voir toutes les tâches
    </Button>
  )
  const allTasksLink = isLegacy ? (
    <a ref={allTasksRef} href={buildV3Url('/tasks')}>
      {allTasksButton}
    </a>
  ) : (
    <Link ref={allTasksRef} to="/tasks" onClick={() => setOpen(false)}>
      {allTasksButton}
    </Link>
  )

  // Second click on the header icon while the popover is open: go to the full page,
  // through the footer link so SPA and legacy navigation stay the same as a manual click.
  const onTriggerClick = () => {
    if (isOpen) {
      allTasksRef.current?.click()
      setOpen(false)
      return
    }
    setOpen(true)
  }

  return (
    <>
      <div
        className={clsx(
          classname,
          container,
          overdue > 0 ? toolBarItemAttention : null
        )}
        ref={triggerRef}
        title={countSummary ? `Mes tâches : ${countSummary}` : 'Mes tâches'}
        onClick={onTriggerClick}
      >
        {total > 0 && (
          <span className={toolBarItemAttentionCount}>{total}</span>
        )}
        <FontAwesomeIcon icon={faListCheck} />
      </div>
      <div className={tasksZone}>
        <Dropdown
          isOpen={isOpen}
          onclose={() => setOpen(false)}
          showCloseTrigger={true}
          dropdownRef={triggerRef}
          className={tasksDropdown}
          offsetValue={25}
        >
          <div className={tasksTitle}>
            Mes tâches
            {countSummary && (
              <span className={tasksTitleCount}>{countSummary}</span>
            )}
          </div>
          <div className={tasksStack}>
            {isLoading && !isError && (
              <div className={tasksLoader}>
                <Shimmer width="100%" height={50} />
                <Shimmer width="100%" height={50} />
              </div>
            )}
            {isError && (
              <Alert
                variation={AlertVariation.Danger}
                message="Une erreur est survenue lors de la récupération des tâches"
                onDismiss={false}
              />
            )}
            {!isLoading && !isError && tasks.length === 0 && (
              <NoResult
                message="Aucune tâche à faire"
                isSmall
                icon={faListCheck}
              />
            )}
            {tasks.length > 0 && (
              <div className={taskGridCompact}>
                {tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    compact
                    onOpen={() => setOpen(false)}
                  />
                ))}
              </div>
            )}
          </div>
          <ButtonGroup align="right" className={tasksFooter}>
            {allTasksLink}
            <Button
              variation={ButtonVariation.Primary}
              buttonSize="small"
              onClick={openCreation}
            >
              <FontAwesomeIcon icon={faPlus} /> Nouvelle tâche
            </Button>
          </ButtonGroup>
        </Dropdown>
      </div>
    </>
  )
}
